import React, { useState, useEffect } from 'react';
import {
  GitBranch,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  Clock,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ringGroupsAPI, extensionsAPI } from '../services/api';

const RingGroups = () => {
  const [ringGroups, setRingGroups] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    extension: '',
    strategy: 'ring-all',
    ring_time: 20,
    members: [],
    destination: 'voicemail',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [groupsRes, extsRes] = await Promise.all([
        ringGroupsAPI.list(),
        extensionsAPI.list()
      ]);
      setRingGroups(groupsRes.data);
      setExtensions(extsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = ringGroups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.extension.includes(searchQuery)
  );

  const getStrategyLabel = (strategy) => {
    const strategies = {
      'ring-all': 'Ring All',
      'round-robin': 'Round Robin',
      'sequential': 'Sequential',
      'random': 'Random',
      'longest-idle': 'Longest Idle'
    };
    return strategies[strategy] || strategy;
  };

  const getMemberName = (memberId) => {
    const ext = extensions.find((e) => e.id === memberId);
    return ext ? ext.name : memberId;
  };

  const handleMemberToggle = (memberId, isNew = true) => {
    if (isNew) {
      if (newGroup.members.includes(memberId)) {
        setNewGroup({ ...newGroup, members: newGroup.members.filter((m) => m !== memberId) });
      } else {
        setNewGroup({ ...newGroup, members: [...newGroup.members, memberId] });
      }
    } else {
      if (selectedGroup.members.includes(memberId)) {
        setSelectedGroup({ ...selectedGroup, members: selectedGroup.members.filter((m) => m !== memberId) });
      } else {
        setSelectedGroup({ ...selectedGroup, members: [...selectedGroup.members, memberId] });
      }
    }
  };

  const handleAddGroup = async () => {
    try {
      await ringGroupsAPI.create(newGroup);
      setIsAddModalOpen(false);
      setNewGroup({
        name: '',
        extension: '',
        strategy: 'ring-all',
        ring_time: 20,
        members: [],
        destination: 'voicemail',
        status: 'active'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating ring group:', error);
    }
  };

  const handleEditGroup = async () => {
    try {
      await ringGroupsAPI.update(selectedGroup.id, {
        name: selectedGroup.name,
        strategy: selectedGroup.strategy,
        ring_time: selectedGroup.ring_time,
        members: selectedGroup.members,
        destination: selectedGroup.destination,
        status: selectedGroup.status
      });
      setIsEditModalOpen(false);
      setSelectedGroup(null);
      fetchData();
    } catch (error) {
      console.error('Error updating ring group:', error);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await ringGroupsAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting ring group:', error);
    }
  };

  const userExtensions = extensions.filter((ext) => ext.type === 'user');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Ring Groups</h1>
          <p className="text-gray-400 mt-1">Configure ring groups for call distribution</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Ring Group
        </Button>
      </div>

      {/* Search */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search ring groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Ring Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredGroups.map((group) => (
          <Card key={group.id} className="bg-[#12121a] border-[#1e1e2d] hover:border-[#2a2a3d] transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <GitBranch className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{group.name}</h3>
                    <p className="text-sm text-gray-400">Ext: {group.extension}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={group.status === 'active'
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                  }>
                    {group.status}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedGroup(group);
                          setIsEditModalOpen(true);
                        }}
                        className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteGroup(group.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Strategy</span>
                  <Badge variant="outline" className="border-[#2a2a3d] text-white">
                    {getStrategyLabel(group.strategy)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Ring Time</span>
                  <span className="text-white flex items-center gap-1">
                    <Clock className="w-4 h-4 text-gray-500" />
                    {group.ring_time}s
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">No Answer</span>
                  <span className="text-white">{group.destination}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#1e1e2d]">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-400">Members ({group.members.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.members.map((memberId) => (
                    <Badge key={memberId} variant="secondary" className="bg-[#1e1e2d] text-white">
                      {getMemberName(memberId)} ({memberId})
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredGroups.length === 0 && (
          <Card className="bg-[#12121a] border-[#1e1e2d] col-span-2">
            <CardContent className="p-12 text-center">
              <GitBranch className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No ring groups found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Ring Group Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Ring Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Group Name</Label>
                <Input
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="Sales Team"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Extension</Label>
                <Input
                  value={newGroup.extension}
                  onChange={(e) => setNewGroup({ ...newGroup, extension: e.target.value })}
                  placeholder="600"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Ring Strategy</Label>
                <Select
                  value={newGroup.strategy}
                  onValueChange={(value) => setNewGroup({ ...newGroup, strategy: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    <SelectItem value="ring-all">Ring All</SelectItem>
                    <SelectItem value="round-robin">Round Robin</SelectItem>
                    <SelectItem value="sequential">Sequential</SelectItem>
                    <SelectItem value="random">Random</SelectItem>
                    <SelectItem value="longest-idle">Longest Idle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Ring Time (seconds)</Label>
                <Input
                  type="number"
                  value={newGroup.ring_time}
                  onChange={(e) => setNewGroup({ ...newGroup, ring_time: parseInt(e.target.value) })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">No Answer Destination</Label>
              <Select
                value={newGroup.destination}
                onValueChange={(value) => setNewGroup({ ...newGroup, destination: value })}
              >
                <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                  <SelectItem value="voicemail">Voicemail</SelectItem>
                  <SelectItem value="hangup">Hang Up</SelectItem>
                  <SelectItem value="extension:1011">Extension: Reception</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Members</Label>
              <div className="bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                {userExtensions.map((ext) => (
                  <div key={ext.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`new-${ext.id}`}
                      checked={newGroup.members.includes(ext.id)}
                      onCheckedChange={() => handleMemberToggle(ext.id, true)}
                    />
                    <label htmlFor={`new-${ext.id}`} className="text-sm text-white cursor-pointer">
                      {ext.name} ({ext.id})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddGroup} className="bg-blue-600 hover:bg-blue-700">
              Add Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Ring Group Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Ring Group</DialogTitle>
          </DialogHeader>
          {selectedGroup && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Group Name</Label>
                  <Input
                    value={selectedGroup.name}
                    onChange={(e) => setSelectedGroup({ ...selectedGroup, name: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Extension</Label>
                  <Input
                    value={selectedGroup.extension}
                    disabled
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white opacity-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Ring Strategy</Label>
                  <Select
                    value={selectedGroup.strategy}
                    onValueChange={(value) => setSelectedGroup({ ...selectedGroup, strategy: value })}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="ring-all">Ring All</SelectItem>
                      <SelectItem value="round-robin">Round Robin</SelectItem>
                      <SelectItem value="sequential">Sequential</SelectItem>
                      <SelectItem value="random">Random</SelectItem>
                      <SelectItem value="longest-idle">Longest Idle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Ring Time (seconds)</Label>
                  <Input
                    type="number"
                    value={selectedGroup.ring_time}
                    onChange={(e) => setSelectedGroup({ ...selectedGroup, ring_time: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Status</Label>
                <Select
                  value={selectedGroup.status}
                  onValueChange={(value) => setSelectedGroup({ ...selectedGroup, status: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Members</Label>
                <div className="bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                  {userExtensions.map((ext) => (
                    <div key={ext.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={`edit-${ext.id}`}
                        checked={selectedGroup.members.includes(ext.id)}
                        onCheckedChange={() => handleMemberToggle(ext.id, false)}
                      />
                      <label htmlFor={`edit-${ext.id}`} className="text-sm text-white cursor-pointer">
                        {ext.name} ({ext.id})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditGroup} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RingGroups;
